import { useEffect, useState } from "react";
import API_BASE from "../../../../config";
import { authHeaders, fmtCurrency, fmtDateShort } from "../profileUtils";
import { TabSpinner, TabError, TabEmpty } from "./FamilyTab";
import s from "./BooksTab.module.css";

export default function BooksTab({ studentId }) {
  const [purchases, setPurchases] = useState([]);
  const [books,     setBooks]     = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState("");

  useEffect(() => {
    if (!studentId) return;
    let cancelled = false;
    setLoading(true); setError("");

    Promise.all([
      fetch(`${API_BASE}/api/student-book-purchases?studentId=${studentId}`, { headers: authHeaders() })
        .then(r => r.ok ? r.json() : []),
      fetch(`${API_BASE}/api/books`, { headers: authHeaders() })
        .then(r => r.ok ? r.json() : []).catch(() => []),
    ])
      .then(([purchaseData, bookData]) => {
        if (cancelled) return;
        setPurchases(Array.isArray(purchaseData) ? purchaseData : []);
        setBooks(Array.isArray(bookData)         ? bookData     : []);
      })
      .catch(() => { if (!cancelled) setError("Could not load book purchase records."); })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [studentId]);

  if (loading) return <TabSpinner label="Loading book purchases…" />;
  if (error)   return <TabError message={error} />;
  if (purchases.length === 0) return (
    <TabEmpty
      icon="📖"
      title="No book purchases"
      sub="Book purchases made by this student will appear here."
    />
  );

  const bookById = Object.fromEntries(books.map(b => [b.id, b]));
  const sorted   = [...purchases].sort(
    (a, b) => new Date(b.purchasedAt) - new Date(a.purchasedAt)
  );
  const total = purchases.reduce((sum, p) => sum + (Number(p.amount) || 0), 0);

  return (
    <div className={s.root}>
      <div className={s.header}>
        <span className={s.countText}>
          {purchases.length} book{purchases.length !== 1 ? "s" : ""} purchased
        </span>
        <span className={s.totalBadge}>Total {fmtCurrency(total)}</span>
      </div>

      <div className={s.tableWrap}>
        <table className={s.table}>
          <thead>
            <tr>
              <th>Book Title</th>
              <th className={s.thRight}>Amount</th>
              <th>Date Purchased</th>
              <th>Received By</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((p, idx) => {
              const book = bookById[p.bookId];
              return (
                <tr key={p.id} className={s.row} style={{ animationDelay: `${idx * 22}ms` }}>
                  <td className={s.tdBook}>
                    {book?.title || book?.name || `Book #${p.bookId}`}
                  </td>
                  <td className={s.tdAmt}>{fmtCurrency(p.amount)}</td>
                  <td className={s.tdDate}>{fmtDateShort(p.purchasedAt) || "—"}</td>
                  <td className={s.tdStaff}>
                    {p.receivedByStaffId
                      ? <span className={s.staffId}>Staff #{p.receivedByStaffId}</span>
                      : <span className={s.muted}>—</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
